<template>
  <div class="health-section">
    <CategoriesList
      v-if="meta && meta.breadcrumb"
      class="health-section__breadcrumbs"
      :categories="meta.breadcrumb"
      is-breadcrumbs
      last-blocked
      @clicked-on-category="sendAnalytics"
    />
    <ArchivePageTitle
      v-if="category"
      class="health-section__title"
      :title="category.title"
      :icon="category.icon"
    />
    <div class="the-content">
      <TheContent
        v-for="item of filteredContent"
        :key="item.id"
        :elem="item"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { useRoute } from 'vue-router';

  import TheContent from '~/components/content/TheContent';
  import CategoriesList from '~/components/CategoriesList.vue';
  import ArchivePageTitle from '~/components/ArchivePageTitle.vue';

  import { getActivePage, getFullUrl } from '~/utils';
  import type { Category } from '~/api/health/category';
  import type { Meta } from '~/api/health/meta';
  import { usePageStore } from '~/store/pagesData';
  import { trimWpDomainInPlace } from '~/utils/trimWpDomain';
  import { PageSeoInputSchema } from '~/domains/head/seo/api/transformSeo';
  import { parseSeoOrThrow } from '~/domains/head/seo/services/parseSeoOrThrow';
  import { useSeoHead } from '~/domains/head/seo/services/useSeoHead';
  import type { Seo } from '~/domains/head/seo/model/schemas';

  const route = useRoute();
  const pageStore = usePageStore();
  const seo = ref<Seo | null>(null);

  const ARCHIVE_TYPE = 'lh__health-archive';

  // !
  const { $healthApi, $logger, runWithContext } = useNuxtApp();

  const log = $logger.withScope('🏥 section');

  const slug = route.params.slug as string;
  const categoryOrSlug = route.params.categoryOrSlug as string;
  const category = ref<Category | null>(null);
  const meta = ref<Meta | null>(null);

  // страница категории
  if (categoryOrSlug) {
    log('categoryOrSlug', categoryOrSlug);
    const { data, error } = await useAsyncData(() =>
      $healthApi.fetchCategory(categoryOrSlug, 'data,seo,meta', { limit: 30 }),
    );

    if (error.value) throw createError(error.value);

    // если это подкатегория, тогда редиректим на её
    if (data.value?.item?.parent) {
      const redirectUrl = `/health/${data.value.item.parent.slug}/${data.value.item.slug}`;
      log('redirect to subcategory at', redirectUrl);
      runWithContext(() => navigateTo(redirectUrl, { redirectCode: 301 }));
    }

    category.value = data.value.item as Category;
    meta.value = data.value.meta as Meta;
    seo.value = parseSeoOrThrow(PageSeoInputSchema, data.value.seo);
    log('category', category.value);
    log('meta', meta.value);
  } else if (slug) {
    log('slug', slug);
    const page = getActivePage(route);

    const { data: categoryData, error: categoryDataError } = await useAsyncData(() =>
      $healthApi.fetchCategory(slug, 'data'),
    );
    if (categoryDataError.value) throw createError(categoryDataError.value);

    log('categoryData', categoryData);

    const archiveItem = categoryData.value.item?.parsed_description.filter(
      (item) => item.type === 'lh__health-archive',
    );
    const withPosts = Boolean(archiveItem?.length);

    const include = withPosts ? 'data,seo,meta,posts' : 'data,seo,meta';
    const { data: categoryAllData, error: categoryAllDataError } = await useAsyncData(() =>
      $healthApi.fetchCategory(slug, include, { page, limit: 30 }),
    );
    if (categoryAllDataError.value) throw createError(categoryAllDataError.value);

    log('categoryAllData', categoryAllData);

    trimWpDomainInPlace(categoryAllData.value, [
      'items.url',
      'item.parent.url',
      'items.categories.url',
    ]);

    category.value = categoryAllData.value.item as Category;
    meta.value = categoryAllData.value.meta as Meta;
    seo.value = parseSeoOrThrow(PageSeoInputSchema, categoryAllData.value.seo);

    log('category', category.value);
    log('meta', meta.value);

    // * временно. https://app.clickup.com/t/86bzkgcv6
    const startFrom = archiveItem?.length && archiveItem[0]?.attributes?.startFrom;
    const offset = startFrom === 0 ? 0 : startFrom - 1;
    pageStore.addArchiveData({
      data: {
        type: category.value.type,
        id: category.value.id,
        totalPages: meta.value?.total_pages ?? 0,
        articles:
          page === 1
            ? (categoryAllData.value?.items?.slice(offset) ?? [])
            : (categoryAllData.value?.items ?? []),
        title: category.value.title || '',
        icon: category.value.icon,
        seo: seo.value!,
      },
    });
  }

  useSeoHead(seo.value!);

  // Computed: Parsed Content
  const parsedContent = computed(() => {
    return category.value.parsed_description ?? ([] as TContent[]);
  });

  const filteredContent = computed(() => {
    const page = getActivePage(route);
    return page > 1
      ? parsedContent.value.filter((item) => item.type === ARCHIVE_TYPE)
      : parsedContent.value;
  });

  const sendAnalytics = (url: string, title: string): void => {
    const nuxtApp = useNuxtApp();

    if (nuxtApp.$sendYandexMetrika) {
      nuxtApp.$sendYandexMetrika({
        level1: 'Клик_Хлебные крошки',
        level4: getFullUrl(url),
        level6: title,
      });
    } else {
      console.warn('$sendYandexMetrika is not available');
    }
  };
</script>

<style lang="scss" scoped>
  .health-section {
    background: #fff;
    height: 100%;

    &__breadcrumbs {
      padding: 16px;

      @include tablet {
        padding: 16px 24px 8px;
      }
    }

    &__title {
      padding: 0px 16px 20px;

      @include tablet {
        padding: 16px 24px 20px;
      }
    }

    :deep() {
      .the-content > div:not(.tv-health, .article-card-container) {
        border-top: 1px solid #e7e7e7;
      }

      .add-margin-bottom {
        margin-bottom: 0;
      }
      .recommendation-block {
        margin: 0;
        border: 0;

        &__title {
          // undoes the margin from the widgets renderer on home page
          margin: 0;
        }
      }
      .before-after-comments-wrapp {
        border: 0;
      }

      .archive-page-title__text {
        font-size: 28px;
        @include tablet {
          font-size: 36px;
        }
      }

      .lh-large-article-card__wrapper {
        min-width: auto;
        border: 0;

        border-bottom: 1px solid #e7e7e7;
        @include big-tablet {
          width: 100%;
        }
      }
    }
  }
</style>
